import React, { useState } from "react";
import { useOrder } from "../context/OrderContext";
import { useOrderSave } from "../context/OrderSaveContext";
import TablerLayout from "../components/layout/TablerLayout";
import StepperNavButton from "../components/stepper/StepperNavButton";
import FloatingLabelInput from "../components/form_elements/FloatingLabelInput";
import { useStepNavigate } from "../hooks/useStepNavigate";
import { showErrorToast, showSuccessToast } from "../components/toasts/ToastService";
import { useStaticOptions } from "../context/StaticOptionsContext";

const labels = [
    { key: "butyok", label: "Bütyökbőség (cm)" },
    { key: "mar", label: "Marbőség (cm)" },
    { key: "sarok", label: "Sarokbőség (cm)" },
];

const szarasLabels = [
    { key: "bokaboseg", label: "Bokabőség (cm)" },
    { key: "szarboseg", label: "Szárbőség (cm)" },
    { key: "szarmagassag", label: "Szármagasság (cm)" },
    { key: "ikraboseg", label: "Ikrabőség (cm)" },
];

const orderFieldMap = {
    labmeret: "footsize",
    butyok: "butyokboseg",
    mar: "marboseg",
    sarok: "sarokboseg",
};

const Meretvetel = () => {
    const { staticOptions } = useStaticOptions();
    const footSizes = staticOptions?.foot_sizes ?? [];
    const { order, updateOrder } = useOrder();
    const { saveOrder } = useOrderSave();
    const navigate = useStepNavigate();
    const [bal, setBal] = useState({
        labmeret: order.hmeliorder?.hmeliorder_footsize_left || "",
        butyok: order.hmeliorder?.hmeliorder_butyokboseg_left || "",
        mar: order.hmeliorder?.hmeliorder_marboseg_left || "",
        sarok: order.hmeliorder?.hmeliorder_sarokboseg_left || "",
        bokaboseg: order.hmeliorder?.hmeliorder_bokaboseg_left || "",
        szarboseg: order.hmeliorder?.hmeliorder_szarboseg_left || "",
        szarmagassag: order.hmeliorder?.hmeliorder_szarmagassag_left || "",
        ikraboseg: order.hmeliorder?.hmeliorder_ikraboseg_left || "",
    });
    const [jobb, setJobb] = useState({
        labmeret: order.hmeliorder?.hmeliorder_footsize_right || "",
        butyok: order.hmeliorder?.hmeliorder_butyokboseg_right || "",
        mar: order.hmeliorder?.hmeliorder_marboseg_right || "",
        sarok: order.hmeliorder?.hmeliorder_sarokboseg_right || "",
        bokaboseg: order.hmeliorder?.hmeliorder_bokaboseg_right || "",
        szarboseg: order.hmeliorder?.hmeliorder_szarboseg_right || "",
        szarmagassag: order.hmeliorder?.hmeliorder_szarmagassag_right || "",
        ikraboseg: order.hmeliorder?.hmeliorder_ikraboseg_right || "",
    });
    const [loading, setLoading] = useState(false);
    const parsedMaxStemHeight = Number(order?.model_options?.max_stem_height);
    const stemHeightLimit =
        Number.isFinite(parsedMaxStemHeight) && parsedMaxStemHeight > 0 ? parsedMaxStemHeight : null;

    const isBetetOrder = order.hmeliorder?.hmeliorder_depot_prefix === "BETÉT";
    const isHightopShoes = order.hmeliorder?.hmeliorder_is_hightopshoes === 1;
    const activeFields = labels.concat(isHightopShoes ? szarasLabels : []);

    const handleInput = (side, key, rawValue) => {
        if (isBetetOrder && key !== "labmeret") return;
        let value = rawValue;

        if (key === "szarmagassag" && stemHeightLimit !== null) {
            const numericValue = parseFloat(value);
            if (!Number.isNaN(numericValue) && numericValue > stemHeightLimit) {
                value = stemHeightLimit.toString();
            }
        }

        if (/^\d{0,2}(\.\d{0,2})?$/.test(value)) {
            const orderKey = orderFieldMap[key] ?? key;
            if (side === "bal") {
                let uj = { ...bal, [key]: value };

                if (key === "szarmagassag" && parseFloat(value) <= 20) {
                    uj = { ...uj, ikraboseg: "" };
                }

                setBal(uj);
                updateOrder({
                    ...order,
                    hmeliorder: {
                        ...order.hmeliorder,
                        [`hmeliorder_${orderKey}_left`]: value,
                        ...(key === "szarmagassag" && parseFloat(value) <= 20
                            ? { hmeliorder_ikraboseg_left: "" }
                            : {}),
                    },
                });
            } else {
                let uj = { ...jobb, [key]: value };

                if (key === "szarmagassag" && parseFloat(value) <= 20) {
                    uj = { ...uj, ikraboseg: "" };
                }

                setJobb(uj);
                updateOrder({
                    ...order,
                    hmeliorder: {
                        ...order.hmeliorder,
                        [`hmeliorder_${orderKey}_right`]: value,
                        ...(key === "szarmagassag" && parseFloat(value) <= 20
                            ? { hmeliorder_ikraboseg_right: "" }
                            : {}),
                    },
                });
            }
        }
    };

    const handleSaveAndNavigate = async () => {
        try {
            setLoading(true);

            const updatedOrder = {
                ...order,
                hmeliorder: {
                    ...order.hmeliorder,
                    ...Object.fromEntries(
                        Object.keys(bal).map((key) => [
                            `hmeliorder_${orderFieldMap[key] ?? key}_left`,
                            bal[key],
                        ])
                    ),
                    ...Object.fromEntries(
                        Object.keys(jobb).map((key) => [
                            `hmeliorder_${orderFieldMap[key] ?? key}_right`,
                            jobb[key],
                        ])
                    ),
                },
            };

            updateOrder(updatedOrder);

            const result = await saveOrder(updatedOrder);
            if (result.success) {
                showSuccessToast("A rendelés sikeresen mentve!");

                if (order.hmeliorder?.hmeliorder_depot_prefix === "BETÉT") {
                    navigate("/FelolvasoTabla");
                } else {
                    navigate("/Emeles");
                }
            } else {
                showErrorToast("Hiba történt a rendelés mentésekor!");
            }
        } catch (error) {
            console.error("Hiba a rendelés mentésekor:", error);
            showErrorToast("Hiba történt a rendelés mentésekor!");
        } finally {
            setLoading(false);
        }
    };

    const handleEnter = (e, currentId, nextId) => {
        if (e.key === "Enter") {
            e.preventDefault();
            if (nextId) {
                const nextInput = document.getElementById(nextId);
                if (nextInput) {
                    nextInput.focus();
                }
            } else if (allFilled) {
                handleSaveAndNavigate();
            }
        }
    };

    const isIkrabosegActive = (sideValues) => {
        const height = parseFloat(sideValues.szarmagassag);
        return !Number.isNaN(height) && height > 20;
    };

    const isFieldRequired = (sideValues, key) => {
        if (isBetetOrder) return false;
        if (key !== "ikraboseg") return true;
        return isIkrabosegActive(sideValues);
    };

    const allFilled = isBetetOrder
        ? Boolean(bal.labmeret) && Boolean(jobb.labmeret)
        : activeFields.every((l) => {
                const balReq = isFieldRequired(bal, l.key);
                const jobbReq = isFieldRequired(jobb, l.key);
                return (!balReq || bal[l.key]) && (!jobbReq || jobb[l.key]);
            });

    const getFootSizeSign = (size) => {
        const value = parseFloat(size);
        if (!footSizes.length || Number.isNaN(value)) return "-";
        const match = footSizes.find(
            ({ foot_size_from, foot_size_upto }) =>
                value >= parseFloat(foot_size_from) && value <= parseFloat(foot_size_upto)
        );
        return match?.foot_size_sign ?? "-";
    };

    const handleBackClick = () => {
        if (order.hmeliorder?.hmeliorder_depot_prefix == "BETÉT") {
            return "/BetetTartozek";
        } else {
            return "/CsatolandoMedia";
        }
    };

    const getDominantFootSizeSign = () => {
        const leftValue = parseFloat(bal.labmeret);
        const rightValue = parseFloat(jobb.labmeret);

        const safeLeft = Number.isNaN(leftValue) ? null : leftValue;
        const safeRight = Number.isNaN(rightValue) ? null : rightValue;

        if (safeLeft === null && safeRight === null) return "-";
        const dominantValue =
            safeLeft === null ? safeRight :
            safeRight === null ? safeLeft :
            Math.max(safeLeft, safeRight);

        return getFootSizeSign(dominantValue);
    };

    const dominantFootSizeSign = getDominantFootSizeSign();

    return (
        <TablerLayout title="Méretvétel">
            <div className="row justify-content-center">
                <div className="col-md-10">
                    <div className="card-body">
                        <div className="row g-4">
                            <div className="row">
                                <div className="col-12 col-md-6 mb-4 mb-md-0">
                                    <div className="text-center fw-bold fs-5 mb-3">Bal láb</div>
                                    <div className="mb-3">
                                        <FloatingLabelInput
                                            label="Bal láb mérete (öltés)"
                                            id="bal-labmeret"
                                            name="bal-labmeret"
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            max="60"
                                            addon="öltés"
                                            fontSize={25}
                                            value={bal.labmeret}
                                            onChange={(e) => handleInput("bal", "labmeret", e.target.value)}
                                            onKeyDown={(e) => handleEnter(e, "bal-labmeret", "bal-butyok")}
                                        />
                                    </div>
                                    {activeFields.map((l, idx) => (
                                        <div className="mb-3" key={l.key}>
                                            <FloatingLabelInput
                                                label={l.label}
                                                id={`bal-${l.key}`}
                                                name={`bal-${l.key}`}
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max={
                                                    l.key === "szarmagassag" && stemHeightLimit
                                                        ? stemHeightLimit
                                                        : 99
                                                }
                                                addon="cm"
                                                fontSize={25}
                                                value={bal[l.key]}
                                                disabled={
                                                    isBetetOrder ||
                                                    (l.key === "ikraboseg" && !isIkrabosegActive(bal))
                                                }
                                                onChange={(e) => handleInput("bal", l.key, e.target.value)}
                                                onKeyDown={(e) =>
                                                    handleEnter(
                                                        e,
                                                        `bal-${l.key}`,
                                                        idx < activeFields.length - 1
                                                            ? `bal-${activeFields[idx + 1].key}`
                                                            : "jobb-labmeret"
                                                    )
                                                }
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div className="col-12 col-md-6">
                                    <div className="text-center fw-bold fs-5 mb-3">Jobb láb</div>
                                    <div className="mb-3">
                                        <FloatingLabelInput
                                            label="Jobb láb mérete (öltés)"
                                            id="jobb-labmeret"
                                            name="jobb-labmeret"
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            max="50"
                                            addon="öltés"
                                            fontSize={25}
                                            value={jobb.labmeret}
                                            onChange={(e) => handleInput("jobb", "labmeret", e.target.value)}
                                            onKeyDown={(e) => handleEnter(e, "jobb-labmeret", "jobb-butyok")}
                                        />
                                    </div>
                                    {activeFields.map((l, idx) => (
                                        <div className="mb-3" key={l.key}>
                                            <FloatingLabelInput
                                                label={l.label}
                                                id={`jobb-${l.key}`}
                                                name={`jobb-${l.key}`}
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max={
                                                    l.key === "szarmagassag" && stemHeightLimit
                                                        ? stemHeightLimit
                                                        : 99
                                                }
                                                addon="cm"
                                                fontSize={25}
                                                value={jobb[l.key]}
                                                disabled={
                                                    isBetetOrder ||
                                                    (l.key === "ikraboseg" && !isIkrabosegActive(jobb))
                                                }
                                                onChange={(e) => handleInput("jobb", l.key, e.target.value)}
                                                onKeyDown={(e) =>
                                                    handleEnter(
                                                        e,
                                                        `jobb-${l.key}`,
                                                        idx < activeFields.length - 1
                                                            ? `jobb-${activeFields[idx + 1].key}`
                                                            : null
                                                    )
                                                }
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {stemHeightLimit !== null && (
                                <div className="justify-content-center mt-2">
                                    <div className="col-12 col-md-12 row">
                                        <div className="text-center text-red fs-4 fw-semibold px-4 py-2 border rounded-pill bg-light">
                                            Maximum szármagasság: {stemHeightLimit.toFixed(2)} cm
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="justify-content-center mt-4">
                                <div className="col-12 col-md-12 row">
                                    <div className="text-center fs-3 fw-semibold px-4 py-2 border rounded-pill bg-light">
                                        Méret kategória: {dominantFootSizeSign}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="card-footer d-flex justify-content-between">
                        <StepperNavButton
                            side="left"
                            label="Vissza"
                            onClick={() => navigate(handleBackClick())}
                        />
                        <StepperNavButton
                            side="right"
                            label="Tovább"
                            onClick={handleSaveAndNavigate}
                            disabled={!allFilled || loading}
                        />
                    </div>
                </div>
            </div>
        </TablerLayout>
    );
};

export default Meretvetel;
